import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  PeopleTagsContext,
  SelectableTagWithCount,
  TagGroupWithSelectableTags,
  TagLike,
} from "./PeopleTagsContext";
import { useLoggedInAs } from "shared/hooks";
import { useOrgTestData } from "shared/hooks/useOrgTestData";
import {
  ContactRole,
  EnumOwnerType,
  EnumPeopleVisibleToSystemRoles,
  EnumTagEntityTypes,
  EnumTagGroupKey,
  EnumTagGroupType,
  PeopleManySimpleDocument,
  PersonDetailsDocument,
  PersonDetailsQuery,
  TagByIdQuery,
  useOrganizationNameByIdQuery,
  usePersonUpdateVisibleToMutation,
  useTagCreateMutation,
  useTagManyWithCountsQuery,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import { createMultilingual, getMultilingualString } from "shared/utils";
import _ from "lodash";
import { useTagGroupContext } from "shared/contextProviders/tagGroups";
import { usePeopleContext } from "shared/contextProviders";
import { useAppContext } from "useAppContext";

type PeopleTagsContextProviderProps = PropsWithChildren & {
  entityOverride?: { entityId: string; type: EnumOwnerType };
  /** The owner of the tag list, when it differs from the entity being viewed. */
  tagScopeOverride?: { entityId: string; type: EnumOwnerType };
};

export const PeopleTagsContextProvider = ({
  entityOverride,
  tagScopeOverride,
  children,
}: PeopleTagsContextProviderProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "HEADER.TAGS.SYSTEM_ROLES",
  });

  const loggedInAs = useLoggedInAs();
  const entity = entityOverride ?? loggedInAs;
  const tagScope = tagScopeOverride ?? entity;

  const canAssignTags =
    entity?.type === EnumOwnerType.Organization ||
    (entity?.type === EnumOwnerType.Storefront && !!tagScopeOverride);
  const canManageTags =
    canAssignTags &&
    !!loggedInAs?.entityId &&
    loggedInAs.entityId === tagScope?.entityId;

  const { updatePeople } = usePeopleContext();

  const { data: orgNameData } = useOrganizationNameByIdQuery({
    variables: { id: loggedInAs?.entityId ?? "" },
    skip: loggedInAs?.type !== EnumOwnerType.Organization,
  });
  const orgName = orgNameData?.organizationById?.name
    ? getMultilingualString(orgNameData.organizationById.name)
    : null;
  const shortName = orgNameData?.organizationById?.shortName?.trim();
  const displayNameForStaff = shortName || orgName || "";

  // Set the available system roles based on how the user is logged in
  // Order: 1 Whereabouts Users, 2 [Org] Staff, 3 Operators, 4 Customer, 5 Lead, 6 Event Creator
  // Storefront won't show Whereabouts Users, Operator or DMO_STAFF
  const [systemRoles, setSystemRoles] = useState<TagLike[]>([]);
  useEffect(() => {
    if (!entity) {
      setSystemRoles([]);
      return;
    }
    const isOrgOrTnb =
      entity.type === EnumOwnerType.Organization ||
      entity.type === EnumOwnerType.TnbInternal;
    const staffLabel = displayNameForStaff
      ? t("ORG_STAFF", { orgName: displayNameForStaff })
      : t("DMO_STAFF");

    const roles: TagLike[] = [
      {
        id: EnumPeopleVisibleToSystemRoles.Customer,
        value: EnumPeopleVisibleToSystemRoles.Customer,
        label: t("CUSTOMER"),
        selected: false,
      },
      {
        id: EnumPeopleVisibleToSystemRoles.Lead,
        value: EnumPeopleVisibleToSystemRoles.Lead,
        label: t("LEAD"),
        selected: false,
      },
    ];
    if (isOrgOrTnb) {
      // 1, 6, 5, 2, 3, 4
      roles.length = 0;
      roles.push({
        id: EnumPeopleVisibleToSystemRoles.WhereaboutsUser,
        value: EnumPeopleVisibleToSystemRoles.WhereaboutsUser,
        label: t("WHEREABOUTS_USERS"),
        selected: false,
      });
      roles.push({
        id: EnumPeopleVisibleToSystemRoles.DmoStaff,
        value: EnumPeopleVisibleToSystemRoles.DmoStaff,
        label: staffLabel,
        selected: false,
      });
      roles.push({
        id: EnumPeopleVisibleToSystemRoles.Operator,
        value: EnumPeopleVisibleToSystemRoles.Operator,
        label: t("OPERATOR"),
        selected: false,
      });
      roles.push({
        id: EnumPeopleVisibleToSystemRoles.Customer,
        value: EnumPeopleVisibleToSystemRoles.Customer,
        label: t("CUSTOMER"),
        selected: false,
      });
      roles.push({
        id: EnumPeopleVisibleToSystemRoles.Lead,
        value: EnumPeopleVisibleToSystemRoles.Lead,
        label: t("LEAD"),
        selected: false,
      });
      roles.push({
        id: EnumPeopleVisibleToSystemRoles.EventCreator,
        value: EnumPeopleVisibleToSystemRoles.EventCreator,
        label: t("EVENT_CREATOR"),
        selected: false,
      });
      roles.push({
        id: ContactRole.PrimaryContact,
        value: ContactRole.PrimaryContact,
        label: t("PRIMARY_CONTACT"),
        selected: false,
      });
      roles.push({
        id: ContactRole.FinancialContact,
        value: ContactRole.FinancialContact,
        label: t("FINANCIAL_CONTACT"),
        selected: false,
      });
    }
    setSystemRoles(roles);
  }, [entity, t, displayNameForStaff]);

  const { testUserCount, testTagId } = useOrgTestData(
    entity?.type === EnumOwnerType.Organization ? entity.entityId : null,
  );
  const [testUsersSelected, setTestUsersSelected] = useState(false);
  const testUsersTag: TagLike | null =
    testTagId && testUserCount > 0
      ? {
          id: testTagId,
          value: testTagId,
          label: t("TEST_USERS", { count: testUserCount }),
          selected: testUsersSelected,
        }
      : null;

  // Load the tag groups to get the groupId for the organization specific Person tag group

  const {
    loading: tagGroupLoading,
    error: tagGroupError,
    getTagGroup,
  } = useTagGroupContext();
  const tagGroup = useMemo(
    () =>
      getTagGroup({
        type: EnumTagGroupType.OrganizationSpecific,
        key: EnumTagGroupKey.Person,
      }),
    [getTagGroup],
  );

  const tagGroupId = tagGroup?._id ?? "";

  // Store the selected systemRoleIds and organizationTagIds
  const [selectedSystemRoleIds, setSelectedSystemRoleIds] = useState<string[]>(
    [],
  );
  const [selectedOrganizationTagIds, setSelectedOrganizationTagIds] = useState<
    string[]
  >([]);

  // Load the organization specific Person tags from the database
  const {
    data: organizationTagsData,
    loading: organizationTagsLoading,
    error: organizationTagsError,
  } = useTagManyWithCountsQuery({
    variables: {
      filter: {
        tagGroup: [tagGroupId],
        owner: {
          entityId: tagScope?.entityId,
          type: tagScope?.type as EnumOwnerType,
        },
        entityTypes: [EnumTagEntityTypes.Person],
      },
      limit: 10000,
    },
    skip:
      !tagScope ||
      !tagGroupId ||
      tagGroupLoading ||
      (tagGroupError ? true : false),
  });

  // Membership tier tag IDs from the org's metadata (set in Organization Settings)
  const { organizations } = useAppContext();
  const organization = useMemo(() => {
    if (!tagScope?.entityId) return undefined;
    return organizations.find(o => o._id === tagScope.entityId);
  }, [organizations, tagScope?.entityId]);
  const membershipTierTagIdsOrdered = useMemo(() => {
    return (organization?.metadata?.membershipTiers ?? []) as string[];
  }, [organization?.metadata?.membershipTiers]);
  const individualMembershipTierTagIdsOrdered = useMemo(() => {
    return (organization?.metadata?.individualMembershipTiers ??
      []) as string[];
  }, [organization?.metadata?.individualMembershipTiers]);
  const membershipTierTagIds = useMemo(
    () => new Set(membershipTierTagIdsOrdered),
    [membershipTierTagIdsOrdered],
  );
  const individualMembershipTierTagIds = useMemo(
    () => new Set(individualMembershipTierTagIdsOrdered),
    [individualMembershipTierTagIdsOrdered],
  );

  // Load inheritable tags (e.g. listing tags marked as inheritable) owned by the org.
  // entityTypes is required by the generated type but we don't want to filter by it here,
  // so we pass all entity types to make it a no-op.
  const {
    data: inheritableTagsData,
    loading: inheritableTagsLoading,
    error: inheritableTagsError,
  } = useTagManyWithCountsQuery({
    variables: {
      filter: {
        owner: {
          entityId: tagScope?.entityId,
          type: tagScope?.type as EnumOwnerType,
        },
        entityTypes: Object.values(EnumTagEntityTypes),
        inheritable: true,
      },
      limit: 10000,
    },
    skip: !tagScope || tagGroupLoading,
  });

  /**
   * Mutations for adding/removing tags from a person AND for creating/deleting tags
   */
  const [
    personUpdateVisibleToMutation,
    {
      loading: updatePersonVisibleToLoading,
      error: updatePersonVisibleToError,
    },
  ] = usePersonUpdateVisibleToMutation({
    refetchQueries: [PeopleManySimpleDocument, PersonDetailsDocument],
  });
  const [
    createTagMutation,
    { loading: createTagLoading, error: createTagError },
  ] = useTagCreateMutation();

  /**
   * Update the tags for a person
   * - Returns the tags that were updated
   */
  const updateTagsForPerson = useCallback(
    async (person: PersonDetailsQuery["personDetails"], tagIds: string[]) => {
      const { data, errors } = await personUpdateVisibleToMutation({
        variables: {
          id: person?._id ?? "",
          entityId: entity?.entityId,
          type: entity?.type as EnumOwnerType,
          tags: tagIds,
        },
      });
      if (data?.personUpdateVisibleTo?.personInfo && !errors) {
        return tagIds;
      } else {
        return null;
      }
    },
    [entity, personUpdateVisibleToMutation],
  );

  /**
   * Toggles the selected state of a system role
   */
  const toggleSystemRole = useCallback(
    (systemRole: TagLike, selected: boolean) => {
      setSystemRoles(systemRoles =>
        systemRoles.map(role =>
          role.id === systemRole.id ? { ...role, selected } : role,
        ),
      );
      // Update the selectedSystemRoleIds state array
      setSelectedSystemRoleIds(selectedSystemRoleIds =>
        selected
          ? [...selectedSystemRoleIds, systemRole.id]
          : selectedSystemRoleIds.filter(id => id !== systemRole.id),
      );
    },
    [setSystemRoles, setSelectedSystemRoleIds],
  );

  // The options for the category dropdown in the flyouts
  const flyoutCategoryOptions = useMemo(() => {
    if (!tagGroup) return [];

    return [
      {
        value: tagGroup._id,
        label: getMultilingualString(tagGroup.name),
        key: tagGroup.key,
        type: tagGroup.type,
      },
    ];
  }, [tagGroup]);

  // We'll store all of the tag groups with their tags in a state so that we can display them in the flyout
  // And we'll update this array when the organizationTags change or the tagGroup changes
  const [allTagGroupsWithTags, setAllTagGroupsWithTags] = useState<
    TagGroupWithSelectableTags[]
  >([]);
  const [membershipTierTags, setMembershipTierTags] = useState<
    SelectableTagWithCount[]
  >([]);

  // Set the initial allTagGroupsWithTags state when the tagGroup and organizationTags are loaded.
  // Merges native Person tags with inheritable listing tags (deduplicated by _id).
  // Partitions membership tier tags into a separate list, with individual tiers first.
  useEffect(() => {
    if (!tagGroup) return;

    const personTags = organizationTagsData?.tagMany ?? [];
    const personTagIds = new Set(personTags.map(t => t._id));

    const inheritableTags = (inheritableTagsData?.tagMany ?? []).filter(
      t => !personTagIds.has(t._id),
    );

    const allTags = [...personTags, ...inheritableTags];

    const toSelectable = (tag: (typeof allTags)[0]) => ({
      ...tag,
      id: tag._id,
      value: tag._id,
      label: getMultilingualString(tag.name),
      selected: false,
    });

    const customTags: SelectableTagWithCount[] = [];
    const individualMembershipTags: SelectableTagWithCount[] = [];
    const regularMembershipTags: SelectableTagWithCount[] = [];

    for (const tag of allTags) {
      const selectable = toSelectable(tag);
      if (individualMembershipTierTagIds.has(tag._id)) {
        individualMembershipTags.push(selectable);
      } else if (membershipTierTagIds.has(tag._id)) {
        regularMembershipTags.push(selectable);
      } else {
        customTags.push(selectable);
      }
    }

    setAllTagGroupsWithTags([
      {
        tagGroup: { ...tagGroup },
        tags: _.orderBy(customTags, "label", "asc"),
      },
    ]);
    const individualTierOrder = new Map(
      individualMembershipTierTagIdsOrdered.map((id, i) => [id, i]),
    );
    const regularTierOrder = new Map(
      membershipTierTagIdsOrdered.map((id, i) => [id, i]),
    );
    individualMembershipTags.sort(
      (a, b) =>
        (individualTierOrder.get(a.id) ?? 0) -
        (individualTierOrder.get(b.id) ?? 0),
    );
    regularMembershipTags.sort(
      (a, b) =>
        (regularTierOrder.get(a.id) ?? 0) - (regularTierOrder.get(b.id) ?? 0),
    );
    setMembershipTierTags([
      ...individualMembershipTags,
      ...regularMembershipTags,
    ]);
  }, [
    organizationTagsData?.tagMany,
    inheritableTagsData?.tagMany,
    tagGroup,
    individualMembershipTierTagIds,
    individualMembershipTierTagIdsOrdered,
    membershipTierTagIds,
    membershipTierTagIdsOrdered,
  ]);

  /**
   * Add a new tag to the organization
   * - Returns the new TagLike object
   */
  const createTag = useCallback(
    async (label: string) => {
      const { data } = await createTagMutation({
        variables: {
          record: {
            // BACK_OFFICE tags can be used for both operator and event tagging
            entityTypes: [EnumTagEntityTypes.Person],
            name: createMultilingual(label),
            owner: {
              entityId: tagScope?.entityId,
              type: tagScope?.type,
            },
            tagGroup: tagGroupId,
          },
        },
      });
      if (data?.tagCreate?.record) {
        const selectableTagWithCount = {
          ...data.tagCreate.record,
          id: data.tagCreate.record._id,
          value: data.tagCreate.record._id,
          label: getMultilingualString(data.tagCreate.record.name),
          selected: false,
        };
        setAllTagGroupsWithTags(prev =>
          prev.map(tagGroupWithTags =>
            tagGroupWithTags.tagGroup._id === tagGroupId
              ? {
                  ...tagGroupWithTags,
                  tags: _.orderBy(
                    [...tagGroupWithTags.tags, selectableTagWithCount],
                    "label",
                    "asc",
                  ),
                }
              : tagGroupWithTags,
          ),
        );
        return selectableTagWithCount;
      } else {
        return null;
      }
    },
    [createTagMutation, setAllTagGroupsWithTags, tagScope, tagGroupId],
  );

  /**
   * Toggles the selected state of an organization tag
   */
  const toggleOrganizationTag = useCallback(
    (organizationTag: TagLike, selected: boolean) => {
      setAllTagGroupsWithTags(prev =>
        prev.map(tagGroupWithTags =>
          tagGroupWithTags.tagGroup._id === tagGroupId
            ? {
                ...tagGroupWithTags,
                tags: tagGroupWithTags.tags.map(t =>
                  t.id === organizationTag.id ? { ...t, selected } : t,
                ),
              }
            : tagGroupWithTags,
        ),
      );
      setMembershipTierTags(prev =>
        prev.map(t => (t.id === organizationTag.id ? { ...t, selected } : t)),
      );
      setSelectedOrganizationTagIds(selectedOrganizationTagIds =>
        selected
          ? [...selectedOrganizationTagIds, organizationTag.id]
          : selectedOrganizationTagIds.filter(id => id !== organizationTag.id),
      );
    },
    [
      setAllTagGroupsWithTags,
      setMembershipTierTags,
      setSelectedOrganizationTagIds,
      tagGroupId,
    ],
  );

  const toggleTestUsers = useCallback(
    (selected: boolean) => {
      if (!testTagId) return;
      setTestUsersSelected(selected);
      setSelectedOrganizationTagIds(ids =>
        selected
          ? [...ids.filter(id => id !== testTagId), testTagId]
          : ids.filter(id => id !== testTagId),
      );
    },
    [testTagId],
  );

  const [editingTag, setEditingTag] = useState<SelectableTagWithCount | null>(
    null,
  );

  /**
   * When a tag is updated, the tag will be updated in the appropriate tag group
   * @param tag - The tag to update
   * @returns
   */
  const handleTagUpdated = useCallback(
    (tag: TagByIdQuery["tagById"]) => {
      if (!tag) return;

      setAllTagGroupsWithTags(prev =>
        prev.map(tagGroupWithTags =>
          tagGroupWithTags.tagGroup._id === tagGroupId
            ? {
                ...tagGroupWithTags,
                tags: _.orderBy(
                  // Update the name of the existing tag
                  tagGroupWithTags.tags.map(t =>
                    t.id === tag._id
                      ? {
                          ...tag,
                          id: tag._id,
                          value: tag._id,
                          label: getMultilingualString(tag.name),
                          selected: t.selected,
                        }
                      : t,
                  ),
                  "label",
                  "asc",
                ) as SelectableTagWithCount[],
              }
            : tagGroupWithTags,
        ),
      );

      // Update the tag for all
      updatePeople(prev =>
        prev.map(person =>
          person.tags.includes(tag._id)
            ? {
                ...person,
                tags: person.tags.map(t => (t._id === tag._id ? tag : t)),
              }
            : person,
        ),
      );
    },
    [setAllTagGroupsWithTags, updatePeople, tagGroupId],
  );

  /**
   * When a tag is deleted, the tag will be removed from the appropriate tag group
   * @param tag - The tag to delete
   * @returns
   */
  const handleTagDeleted = useCallback(
    (tag: TagByIdQuery["tagById"]) => {
      if (!tag) return;

      setAllTagGroupsWithTags(prev =>
        prev.map(tagGroupWithTags =>
          tagGroupWithTags.tagGroup._id === tagGroupId
            ? {
                ...tagGroupWithTags,
                // Remove the tag from the tags array
                tags: tagGroupWithTags.tags.filter(
                  t => t._id !== tag._id,
                ) as SelectableTagWithCount[],
              }
            : tagGroupWithTags,
        ),
      );

      // Remove the tag from all people
      updatePeople(prev =>
        prev.map(person =>
          person.tags.includes(tag._id)
            ? { ...person, tags: person.tags.filter(t => t._id !== tag._id) }
            : person,
        ),
      );
    },
    [updatePeople, setAllTagGroupsWithTags, tagGroupId],
  );

  /**
   * When a tag is split, the tag will be split into two new tags
   * @param tag - The tag to split
   * @param newTags - The new tags
   * @returns
   */
  const handleTagSplitCompleted = useCallback(
    (
      updatedTag: TagByIdQuery["tagById"],
      newTags: TagByIdQuery["tagById"][],
    ) => {
      if (!updatedTag || !newTags || newTags.length === 0) return;

      setAllTagGroupsWithTags(prev =>
        prev.map(tagGroupWithTags =>
          tagGroupWithTags.tagGroup._id === tagGroupId
            ? {
                ...tagGroupWithTags,
                tags: _.orderBy(
                  [
                    // Update the name of the existing tag
                    ...tagGroupWithTags.tags.map(t =>
                      t._id === updatedTag._id
                        ? {
                            ...updatedTag,
                            id: updatedTag._id,
                            value: updatedTag._id,
                            label: getMultilingualString(updatedTag.name),
                            selected: t.selected,
                          }
                        : t,
                    ),
                    // Add all of the newly created tags
                    ...newTags
                      .filter(t => !!t)
                      .map(t => ({
                        ...t,
                        id: t._id,
                        value: t._id,
                        label: getMultilingualString(t.name),
                        selected: false,
                      })),
                  ],
                  "label",
                  "asc",
                ) as SelectableTagWithCount[],
              }
            : tagGroupWithTags,
        ),
      );

      // Add the new tags to all people
      updatePeople(prev =>
        prev.map(person =>
          person.tags.includes(updatedTag._id)
            ? {
                ...person,
                tags: [
                  ...person.tags.map(t =>
                    t._id === updatedTag._id ? updatedTag : t,
                  ),
                  ...newTags.filter(t => !!t).map(t => t),
                ],
              }
            : person,
        ),
      );
    },
    [setAllTagGroupsWithTags, updatePeople, tagGroupId],
  );

  const value = useMemo(
    () => ({
      systemRoles,
      selectedSystemRoleIds,
      resetSelectedSystemRoleIds: () => {
        setSystemRoles(prev =>
          prev.map(role => ({ ...role, selected: false })),
        );
        setSelectedSystemRoleIds([]);
      },
      tagGroup,
      selectedOrganizationTagIds,
      resetSelectedOrganizationTagIds: () => {
        setAllTagGroupsWithTags(prev =>
          prev.map(tagGroupWithTags => ({
            ...tagGroupWithTags,
            tags: tagGroupWithTags.tags.map(tag => ({
              ...tag,
              selected: false,
            })),
          })),
        );
        setMembershipTierTags(prev =>
          prev.map(tag => ({ ...tag, selected: false })),
        );
        setTestUsersSelected(false);
        setSelectedOrganizationTagIds([]);
      },
      testUsersTag,
      toggleTestUsers,
      membershipTierTags,
      organizationTagsLoading:
        organizationTagsLoading || inheritableTagsLoading || tagGroupLoading,
      organizationTagsError:
        organizationTagsError || inheritableTagsError || tagGroupError,
      addingOrRemovingTagLoading: updatePersonVisibleToLoading,
      addingOrRemovingTagError: updatePersonVisibleToError,
      allTagGroupsWithTags,
      editingTag,
      setEditingTag,
      flyoutCategoryOptions,
      onTagUpdated: handleTagUpdated,
      onTagDeleted: handleTagDeleted,
      onTagSplitCompleted: handleTagSplitCompleted,
      onTagMergeCompleted: handleTagDeleted,
      canAssignTags,
      canManageTags,
      createTagLoading,
      createTagError,
      createTag,
      updatingTagsForPersonLoading: updatePersonVisibleToLoading,
      updatingTagsForPersonError: updatePersonVisibleToError,
      updateTagsForPerson,
      toggleSystemRole,
      toggleOrganizationTag,
    }),
    [
      systemRoles,
      selectedSystemRoleIds,
      selectedOrganizationTagIds,
      membershipTierTags,
      organizationTagsLoading,
      organizationTagsError,
      inheritableTagsLoading,
      inheritableTagsError,
      tagGroup,
      tagGroupLoading,
      tagGroupError,
      allTagGroupsWithTags,
      editingTag,
      setEditingTag,
      flyoutCategoryOptions,
      handleTagUpdated,
      handleTagDeleted,
      handleTagSplitCompleted,
      canAssignTags,
      canManageTags,
      createTagLoading,
      createTagError,
      createTag,
      updatePersonVisibleToLoading,
      updatePersonVisibleToError,
      updateTagsForPerson,
      toggleSystemRole,
      toggleOrganizationTag,
      testUsersTag?.id,
      testUsersTag?.label,
      testUsersTag?.selected,
      toggleTestUsers,
    ],
  );

  return (
    <PeopleTagsContext.Provider value={value}>
      {children}
    </PeopleTagsContext.Provider>
  );
};
